import { call, put, takeLatest, select, take } from 'redux-saga/effects';

import {
  LOADING_DATA_START,
  LOADING_DATA_FINISHED,
} from '../reducers/loading.reducer';

import { handleError } from './helper';
import { presentationApi } from '../services';
import {
  PRESENTATION_FETCH,
  PRESENTATION_FETCH_SUCCESS,
  PRESENTATION_FETCH_FAILURE,
  PRESENTATION_SAVE,
  PRESENTATION_SAVE_SUCCESS,
  PRESENTATION_SAVE_FAILURE,
  PRESENTATION_ADD_EXTENSIONS,
  PRESENTATION_REMOVE_EXTENSION,
  PRESENTATION_REMOVE_EXTENSION_SUCCESS,
  PRESENTATION_REMOVE_EXTENSION_FAILURE,
  PRESENTATION_ADD_EXTENSIONS_FAILURE,
  PRESENTATION_ADD_EXTENSIONS_SUCCESS,
  PRESENTATION_SHARED_FETCH,
  PRESENTATION_SHARED_FETCH_SUCCESS,
  PRESENTATION_EDIT,
  PRESENTATION_SHARES_FETCH,
  PRESENTATION_SHARES_FETCH_SUCCESS,
  PRESENTATION_SHARES_FETCH_FAILURE,
  PRESENTATION_REMOVE_SHARE,
  PRESENTATION_REMOVE_SHARE_SUCCESS,
  PRESENTATION_REMOVE_SHARE_FAILURE,
  PRESENTATION_FAVOURITE,
  PRESENTATION_FAVOURITE_FAILURE,
  PRESENTATION_FAVOURITE_SUCCESS,
  PRESENTATION_TREE_CHANGE,
  PRESENTATION_ADD_NEW,
} from '../reducers/presentation.reducer';
import * as presentationActions from '../reducers/presentation.reducer';
import {
  PRESENTATIONS_SAVE_PRESENTATION_FAILURE,
  PRESENTATIONS_SAVE_PRESENTATION_SUCCESS,
} from '../reducers/presentations.reducer';
import * as extensionActions from '../reducers/extension.reducer';
import * as extensionSelectors from '../selectors/extension.selectors';
import { showNotification } from '../helpers/notification';

const getExtensions = state => {
  const {
    presentation: { directory },
  } = state;
  const extensions = [];

  const getExtensionsFromChild = child => {
    if (child.isExtension) {
      extensions.push(child.id);
    } else if (child.children && child.children.length) {
      child.children.forEach(getExtensionsFromChild);
    }
  };

  directory.children.forEach(getExtensionsFromChild);
  return extensions;
};

const selectPresentation = state => state.presentation;

const presentationIsFetched = state =>
  Object.keys(state.presentation).length > 0;

function* addNewPresentationRequest({ payload: { history } }) {
  const presentation = { name: 'Unnamed deck' };

  try {
    yield put({ type: LOADING_DATA_START });
    const { data } = yield call(presentationApi.createPresentation, {
      presentation,
    });
    history.history.push(`decks/${data.id}/edit`);
    yield put({ type: PRESENTATION_SAVE_SUCCESS, payload: data });
  } catch (error) {
    const message = yield* handleError(error);
    yield put({ type: PRESENTATION_SAVE_FAILURE, payload: message });
  }
  yield put({ type: LOADING_DATA_FINISHED });
}

function* savePresentationRequest({ payload: { presentation } }) {
  try {
    yield put({ type: LOADING_DATA_START });
    const { data } = presentation.id
      ? yield call(presentationApi.editPresentation, { presentation })
      : yield call(presentationApi.createPresentation, { presentation });

    yield put({ type: PRESENTATION_SAVE_SUCCESS, payload: data });
    yield put({ type: PRESENTATIONS_SAVE_PRESENTATION_SUCCESS, payload: data });
  } catch (error) {
    const message = yield* handleError(error);
    yield put({ type: PRESENTATION_SAVE_FAILURE, payload: message });
    yield put({
      type: PRESENTATIONS_SAVE_PRESENTATION_FAILURE,
      payload: message,
    });
  }
  yield put({ type: LOADING_DATA_FINISHED });
}

function* fetchPresentationRequest({ payload: { id } }) {
  try {
    yield put({ type: LOADING_DATA_START });
    const { data } = yield call(presentationApi.fetchPresentation, { id });
    yield put({ type: PRESENTATION_FETCH_SUCCESS, payload: data });
  } catch (error) {
    const message = yield* handleError(error);
    yield put({ type: PRESENTATION_FETCH_FAILURE, payload: message });
  }
  yield put({ type: LOADING_DATA_FINISHED });
}

function* fetchPresentationSharesRequest({ payload: { id } }) {
  try {
    yield put({ type: LOADING_DATA_START });
    const { data } = yield call(presentationApi.fetchPresentationShares, {
      id,
    });
    yield put({ type: PRESENTATION_SHARES_FETCH_SUCCESS, payload: data });
  } catch (error) {
    const message = yield* handleError(error);
    yield put({ type: PRESENTATION_SHARES_FETCH_FAILURE, payload: message });
  }
  yield put({ type: LOADING_DATA_FINISHED });
}

function* fetchSharedPresentationRequest({ payload: { token } }) {
  try {
    if (yield select(presentationIsFetched)) {
      return;
    }

    yield put({ type: LOADING_DATA_START });
    const { data } = yield call(presentationApi.fetchSharedPresentation, {
      token,
    });
    yield put({ type: PRESENTATION_SHARED_FETCH_SUCCESS, payload: data });
  } catch (error) {
    showNotification('Share link expired.');
  }
  yield put({ type: LOADING_DATA_FINISHED });
}

function* addExtensionToPresentationRequest({ payload: { id, extensions } }) {
  try {
    yield put({ type: LOADING_DATA_START });
    const { data } = yield call(presentationApi.addExtensionToPresentation, {
      id,
      extensions,
    });
    yield put({
      type: PRESENTATION_ADD_EXTENSIONS_SUCCESS,
      payload: { data },
    });
  } catch (error) {
    const message = yield* handleError(error);
    yield put({ type: PRESENTATION_ADD_EXTENSIONS_FAILURE, payload: message });
  }
  yield put({ type: LOADING_DATA_FINISHED });
}

function* removeExtensionFromPresentationRequest({
  payload: { id, extensionId },
}) {
  try {
    yield put({ type: LOADING_DATA_START });
    yield call(presentationApi.removeExtensionFromPresentation, {
      id,
      extensionId,
    });
    yield put({
      type: PRESENTATION_REMOVE_EXTENSION_SUCCESS,
      payload: { extensionId },
    });
  } catch (error) {
    const message = yield* handleError(error);
    yield put({
      type: PRESENTATION_REMOVE_EXTENSION_FAILURE,
      payload: message,
    });
  }
  yield put({ type: LOADING_DATA_FINISHED });
}

function* favouriteShareRequest({ payload: { token, id, favourite } }) {
  try {
    yield put({ type: LOADING_DATA_START });
    const { data } = yield call(presentationApi.favouriteShare, {
      token,
      id,
      favourite,
    });
    yield put({ type: PRESENTATION_FAVOURITE_SUCCESS, payload: data });
  } catch (error) {
    const message = yield* handleError(error);
    yield put({ type: PRESENTATION_FAVOURITE_FAILURE, payload: message });
  }
  yield put({ type: LOADING_DATA_FINISHED });
}

function* editPresentationRequest({ payload: { presentation } }) {
  try {
    yield put({ type: LOADING_DATA_START });

    const { data } = yield call(presentationApi.editPresentation, {
      presentation,
    });

    yield put({ type: PRESENTATION_SAVE_SUCCESS, payload: data });
    yield put({ type: PRESENTATIONS_SAVE_PRESENTATION_SUCCESS, payload: data });

    let extensions = yield select(getExtensions);
    if (extensions.length)
      yield put({
        type: PRESENTATION_ADD_EXTENSIONS,
        payload: {
          id: presentation.id,
          extensions: { ...presentation.config },
        },
      });
  } catch (error) {
    const message = yield* handleError(error);
    yield put({ type: PRESENTATION_SAVE_FAILURE, payload: message });
    yield put({
      type: PRESENTATIONS_SAVE_PRESENTATION_FAILURE,
      payload: message,
    });
  }
  yield put({ type: LOADING_DATA_FINISHED });
}

function* presentationTreeChangeRequest({ payload: { id, children } }) {
  try {
    const presentation = yield select(selectPresentation);

    yield put(extensionActions.fetchExtension(id));
    yield take(extensionActions.EXTENSION_FETCH_SUCCESS);

    const config = {
      ...presentation.config,
    };

    yield put(
      presentationActions.editPresentation({
        ...presentation,
        config,
        directory: {
          ...presentation.directory,
          children,
        },
      })
    );

    const filteredMediaToSelectFrom = yield select(
      extensionSelectors.getFilteredMediaToSelectFrom
    );
    if (filteredMediaToSelectFrom.length) {
      yield put(presentationActions.presentationOpenSelectMediaDialog());
    }
  } catch (error) {}
}

function* removeShareRequest({ payload: { shareId, presentationId } }) {
  try {
    yield put({ type: LOADING_DATA_START });
    yield call(presentationApi.deletePresentationShare, {
      shareId,
      presentationId,
    });
    yield put({
      type: PRESENTATION_REMOVE_SHARE_SUCCESS,
      payload: { shareId },
    });
  } catch (error) {
    const message = yield* handleError(error);
    yield put({
      type: PRESENTATION_REMOVE_SHARE_FAILURE,
      payload: message,
    });
  }
  yield put({ type: LOADING_DATA_FINISHED });
}

export function* savePresentation() {
  yield takeLatest(PRESENTATION_SAVE, savePresentationRequest);
}

export function* fetchPresentation() {
  yield takeLatest(PRESENTATION_FETCH, fetchPresentationRequest);
}

export function* fetchPresentationShares() {
  yield takeLatest(PRESENTATION_SHARES_FETCH, fetchPresentationSharesRequest);
}

export function* addNewPresentation() {
  yield takeLatest(PRESENTATION_ADD_NEW, addNewPresentationRequest);
}

export function* fetchSharedPresentation() {
  yield takeLatest(PRESENTATION_SHARED_FETCH, fetchSharedPresentationRequest);
}

export function* selectExtensionToSelectMediaDialog() {
  yield takeLatest(PRESENTATION_TREE_CHANGE, presentationTreeChangeRequest);
}

export function* editPresentation() {
  yield takeLatest(PRESENTATION_EDIT, editPresentationRequest);
}

export function* addExtensionToPresentation() {
  yield takeLatest(
    PRESENTATION_ADD_EXTENSIONS,
    addExtensionToPresentationRequest
  );
}

export function* removeExtensionFromPresentation() {
  yield takeLatest(
    PRESENTATION_REMOVE_EXTENSION,
    removeExtensionFromPresentationRequest
  );
}

export function* removeShare() {
  yield takeLatest(PRESENTATION_REMOVE_SHARE, removeShareRequest);
}

export function* favouriteShare() {
  yield takeLatest(PRESENTATION_FAVOURITE, favouriteShareRequest);
}



// WEBPACK FOOTER //
// ./src/sagas/presentation.saga.js